/**
 * The four reads the kids desk lives on, cached.
 *
 * Injected into the shared `baseApi` from beside the screen rather than added to
 * `Redux/Slices/spacesApi.ts`: that slice is owned by the floor-map workstream
 * and its whole design is the structure/state split for **tables**. A kids-area
 * occupancy count and a waiting-list ticket are neither, and dropping them in
 * there would put two unrelated caching rules in one file. `injectEndpoints` is
 * exactly the seam for this — one cache, one tag namespace, endpoints declared
 * where they are used.
 *
 * Tags are the existing `Resource` / `ResourceSession` ones, so nothing new had
 * to be added to `API_TAGS` (a file this agent does not own) and
 * `useSpacesRealtime`'s existing `invalidateTags([...ResourceSession])` — which
 * has never had a query to invalidate — starts doing real work the moment this
 * screen is mounted.
 *
 * ## Writes are not here, deliberately
 *
 * Check-in, check-out, seat and cancel go through the axios client in the
 * dialogs, the same way the kitchen board's writes do: `baseApi` "is deliberately
 * NOT a write path" in this codebase, and the kids-area writes carry a child and
 * their guardians rather than a plain id. What this module owns is the
 * invalidation those writes report through — {@link invalidateKidsArea}.
 */
import { baseApi } from '@/Redux/baseApi';
import type { ResourceRow, SessionRow } from './lib/sessions';
import type { KidsOccupancy, WaitingListTicket } from './components/kids-area';

export type { KidsOccupancy, WaitingListTicket };

/** `paginate()` answers `{ data, meta }`; a bare list endpoint answers an array. */
function rowsOf<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return ((payload as { data?: T[] } | null)?.data ?? []) as T[];
}

/**
 * The queue is a *fold over `AuditLog`*, not a table — see
 * `kids-waiting-list.service.ts`. Two consequences reach this file:
 *
 *  - Nothing emits when a ticket is joined, called, seated or cancelled. The
 *    only notification the service raises is addressed to a guardian, and it
 *    reaches them only if they are also a customer — `RealtimeService.onNotification`
 *    drops anything without a `userId`, so no staff socket ever hears it. A queue
 *    that another terminal changed can therefore only be discovered by asking.
 *  - Asking is cheap: `(tenantId, entityType, entityId)` is indexed and the fold
 *    is bounded to a 12-hour window, so this is one indexed read.
 *
 * Hence a poll, on the queue alone, at the interval below. It is the honest
 * price of the fold, not a workaround for it.
 */
export const WAITING_LIST_POLL_MS = 20_000;

/** Occupancy and sessions are event-driven; this is the belt to that's braces. */
const LIVE_TTL = 30;
/** A kids area is drawn on the floor map weeks apart. */
const STRUCTURE_TTL = 60 * 60;

export interface KidsSessionsArgs {
  resourceId: string;
  status: 'ACTIVE' | 'PAUSED';
}

export const kidsStationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** Every kids area in the venue — the picker, and nothing else. */
    kidsAreas: build.query<ResourceRow[], void>({
      query: () => ({
        url: '/resources',
        params: { type: 'KIDS_AREA', limit: 200, sortBy: 'name', sortOrder: 'asc' },
      }),
      transformResponse: (response: unknown) => rowsOf<ResourceRow>(response),
      keepUnusedDataFor: STRUCTURE_TTL,
      providesTags: (result) => [
        { type: 'Resource' as const, id: 'KIDS_AREAS' },
        ...(result ?? []).map((resource) => ({ type: 'Resource' as const, id: resource.id })),
      ],
    }),

    /**
     * The children in one area. ACTIVE and PAUSED are two calls because
     * `ListSessionsDto.status` takes one enum member — the same shape
     * `useResourceBoard` already uses, kept rather than replaced with an
     * unfiltered read that a busy day's closed sessions would push the live ones
     * out of.
     */
    kidsAreaSessions: build.query<SessionRow[], KidsSessionsArgs>({
      query: ({ resourceId, status }) => ({
        url: '/resource-sessions',
        params: { resourceId, status, limit: 200 },
      }),
      transformResponse: (response: unknown) => rowsOf<SessionRow>(response),
      keepUnusedDataFor: LIVE_TTL,
      providesTags: (_result, _error, args) => [
        { type: 'ResourceSession' as const, id: args.resourceId },
        { type: 'ResourceSession' as const, id: 'LIST' },
      ],
    }),

    kidsOccupancy: build.query<KidsOccupancy, string>({
      query: (resourceId) => ({ url: `/kids-area/occupancy/${resourceId}` }),
      keepUnusedDataFor: LIVE_TTL,
      providesTags: (_result, _error, resourceId) => [
        { type: 'ResourceSession' as const, id: resourceId },
      ],
    }),

    kidsWaitingList: build.query<WaitingListTicket[], string>({
      query: (resourceId) => ({ url: '/kids-area/waiting-list', params: { resourceId } }),
      transformResponse: (response: unknown) => rowsOf<WaitingListTicket>(response),
      keepUnusedDataFor: LIVE_TTL,
      providesTags: (_result, _error, resourceId) => [
        { type: 'ResourceSession' as const, id: `queue:${resourceId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useKidsAreasQuery,
  useKidsAreaSessionsQuery,
  useKidsOccupancyQuery,
  useKidsWaitingListQuery,
} = kidsStationApi;

/**
 * Everything about one area is now suspect — a check-in, a check-out, a seat, a
 * cancelled ticket, or a `resource_session.*` event.
 *
 * One helper rather than four call sites spelling four tag arrays: the queue and
 * the occupancy move together with the sessions in every case, because seating a
 * ticket *is* starting a session and ending one auto-calls the next family
 * (`callNextForFreeSlots`, from `endSession`). Anything narrower would leave the
 * queue on screen claiming a place that has already been offered.
 */
export function invalidateKidsArea(resourceId: string) {
  return kidsStationApi.util.invalidateTags([
    { type: 'ResourceSession' as const, id: resourceId },
    { type: 'ResourceSession' as const, id: `queue:${resourceId}` },
    { type: 'ResourceSession' as const, id: 'LIST' },
  ]);
}
